import React from 'react';
import { Button, TextField, Typography, useMediaQuery } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isNaN, isNumber } from 'lodash';

import { IProduct, PriceValue } from '../../../../../interfaces/product';
import { toAbsoluteUrl } from '../../../../../../_base';
import { actions as cartActions } from '../../../../../store/ducks/cart.duck';
import {
  selectAddProductLoading,
  selectCart,
  selectGuestCart,
  selectIsAuthorized,
  selectMe,
} from '../../../cart/selectors';
import { useDefineUserRole } from '../../../../../hooks';
import { getMaxQuantity, thousands } from '../../../../../utils/utils';
import { calculatePrice } from '../../../cart/utils';

export default function ProductCount({
  product,
  style,
  showPrice,
  priceBold,
  priceValue,
}: {
  product?: IProduct;
  style?: React.CSSProperties;
  showPrice?: boolean;
  priceBold?: boolean;
  priceValue: PriceValue;
}) {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const cart = useSelector(selectCart);
  const guestCart = useSelector(selectGuestCart);
  const isAuthorized = useSelector(selectIsAuthorized);
  const addProductLoading = useSelector(selectAddProductLoading);
  const me = useSelector(selectMe);
  const [editable, setEditable] = React.useState(false);
  const [value, setValue] = React.useState(1);

  const isBuyer = useDefineUserRole(me, ['ROLE_BUYER', 'ROLE_BUYER_STAFF']);
  const isTablet = useMediaQuery('(max-width:1024px)');

  if (!product) {
    return null;
  }

  const cartProduct = cart?.items.find(
    item => item.vendor_id === priceValue.vendor?.id && item.product.id === product.id
  );
  const guestCartProduct = guestCart?.items.find(
    item =>
      item.product.id === product.id && item.priceValue?.vendor?.id === priceValue.vendor?.id
  );

  const cartProductCount = cartProduct?.count || 0;

  const guestCartProductCount = guestCartProduct?.count || 0;

  const count = isAuthorized ? cartProductCount || 0 : guestCartProductCount || 0;
  const price = isAuthorized
    ? (cartProduct?.product_price || 0) * count
    : calculatePrice(count, guestCartProduct?.priceValue?.prices) * count;

  const maxQuantite = getMaxQuantity(
    priceValue?.vendor?.quantity_level,
    priceValue?.vendor?.second_quantity_level
  )
    ? 10000000000000
    : 0;
  const quantity =
    (priceValue.vendor?.order_quantity || 0) + (priceValue.vendor?.stock_quantity || 0);
  const minCount = priceValue.prices?.[0]?.amount || 0;
  const maxCount = quantity || maxQuantite;

  const handleEditable = () => {
    if (value === 0) {
      if (isAuthorized) {
        dispatch(
          cartActions.setProductCountRequest({
            product_id: product.id || 0,
            count: 0,
            vendor_id: priceValue.vendor?.id,
          })
        );
      } else {
        dispatch(
          cartActions.setCountGuestCart({
            product_id: product.id || 0,
            count: 0,
            type: 'dec',
            priceValue,
          })
        );
      }
    } else if (isAuthorized) {
      const minValue = value < minCount ? 0 : value;
      dispatch(
        cartActions.setProductCountRequest({
          product_id: product.id || 0,
          count: value > maxCount ? maxCount : minValue,
          vendor_id: priceValue.vendor?.id,
        })
      );
    } else {
      const minValue = value < minCount ? 0 : value;
      const countValue = value > maxCount ? maxCount : minValue;
      if (count === 0) {
        dispatch(
          cartActions.setProductGuestCart({
            data: product,
            type: 'cart',
            count: countValue,
            priceValue,
          })
        );
      } else {
        dispatch(
          cartActions.setCountGuestCart({
            product_id: product.id || 0,
            count: countValue,
            type: countValue > count ? 'inc' : 'dec',
            priceValue,
          })
        );
      }
    }
  };

  const addToBasket = () => {
    const newCountInc = +count + 1;
    const minValueInc = minCount > newCountInc ? minCount : 1;
    const countValueInc = newCountInc > maxCount ? maxCount : minValueInc;
    isAuthorized
      ? dispatch(
          cartActions.addProductRequest({
            product_id: product.id || 0,
            count: countValueInc,
            vendor_id: priceValue.vendor?.id,
          })
        )
      : dispatch(
          count > 0
            ? cartActions.setCountGuestCart({
                product_id: product.id || 0,
                count: countValueInc + count,
                type: countValueInc > count ? 'inc' : 'dec',
                priceValue,
              })
            : cartActions.setProductGuestCart({
                data: product,
                type: 'cart',
                count: countValueInc,
                priceValue,
              })
        );
  };

  if (!isBuyer && isAuthorized) return null;

  if (maxCount === 0) return null;

  return (
    <div className={classes.buttonsContainer} style={style}>
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        <div className={classes.countContainer}>
          <Button
            disabled={addProductLoading || count < 1}
            onClick={() => {
              const newCount = +count - 1;
              const minValue = newCount < minCount ? 0 : newCount;
              const countValue = newCount > maxCount ? maxCount : minValue;
              isAuthorized
                ? dispatch(
                    cartActions.setProductCountRequest({
                      product_id: product.id || 0,
                      count: countValue,
                      vendor_id: priceValue.vendor?.id,
                    })
                  )
                : dispatch(
                    cartActions.setCountGuestCart({
                      product_id: product.id || 0,
                      count: countValue,
                      type: countValue > count ? 'inc' : 'dec',
                      priceValue,
                    })
                  );
            }}
            color='primary'
          >
            <img src={toAbsoluteUrl('/media/icons/cart-plus-button.svg')} alt='minus' />
          </Button>

          {editable ? (
            <TextField
              autoFocus
              margin='normal'
              value={value}
              onChange={e => {
                if (
                  !isNumber(+e.target.value) ||
                  +e.target.value < 0 ||
                  isNaN(+e.target.value)
                ) {
                  setValue(count);
                  return;
                }
                if (e.target.value === '') {
                  setValue(0);
                } else {
                  setValue(+e.target.value);
                }
              }}
              inputProps={{ style: { textAlign: 'center' } }}
              sx={{
                margin: 0,
                padding: 0,
                marginTop: '4px',
                maxWidth: 90,
                border: 'none',
              }}
              onBlur={() => {
                if (value !== count) {
                  if (isNumber(+value) && +value >= 0) {
                    handleEditable();
                  }
                }
                setEditable(false);
              }}
              onKeyUp={event => {
                if (event.key === 'Enter') {
                  if (value !== count) {
                    if (isNumber(+value) && +value >= 0) {
                      handleEditable();
                    }
                  }
                  setEditable(false);
                }
              }}
              variant='outlined'
              autoComplete='off'
            />
          ) : (
            <div
              onClick={() => {
                setEditable(true);
                setValue(count);
              }}
              className={classes.containerCointeText}
            >
              <Typography color='inherit' component='p' className={classes.countText}>
                {count}
              </Typography>
            </div>
          )}
          <Button
            disabled={addProductLoading}
            onClick={() => {
              if (count < 1) {
                addToBasket();
              } else {
                const newCount = +count + 1;
                const minValue = newCount < minCount ? 0 : newCount;
                const countValue = newCount > maxCount ? maxCount : minValue;
                isAuthorized
                  ? dispatch(
                      cartActions.setProductCountRequest({
                        product_id: product.id || 0,
                        count: countValue,
                        vendor_id: priceValue.vendor?.id,
                      })
                    )
                  : dispatch(
                      cartActions.setCountGuestCart({
                        product_id: product.id || 0,
                        count: countValue,
                        type: countValue > count ? 'inc' : 'dec',
                        priceValue,
                      })
                    );
              }
            }}
            color='primary'
          >
            <img src={toAbsoluteUrl('/media/icons/cart-minus-button.svg')} alt='plus' />
          </Button>
        </div>
      </div>
      {(isTablet || (showPrice && price > 0)) && (
        <div className={classes.containerPrice}>
          <Typography
            component='p'
            className={priceBold ? classes.priceBold : classes.price}
            style={{ fontSize: 17 }}
          >
            {thousands(String(price ? price.toFixed(2) : '0'))}
          </Typography>
          <Typography component='p' className={classes.rub}>
            ₽
          </Typography>
        </div>
      )}
    </div>
  );
}

const useStyles = makeStyles()(theme => {
  return {
    countContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      // backgroundColor: '#a3a7b819',
      width: '100%',
      minWidth: 220,
    },
    buttonsContainer: {
      display: 'flex',
      width: '100%',
      borderRadius: 4,
      flexWrap: 'wrap',
      gap: 20,
    },
    containerCointeText: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      width: '100%',
    },
    countText: {
      lineHeight: 1.3,
      display: 'flex',
      alignItems: 'center',
      color: '#181c32',
      fontSize: 20,
      fontWeight: '400',
    },
    price: {
      fontSize: 32,
      fontWeight: theme.typography.fontWeightRegular,
      color: '#181c32',
    },
    priceBold: {
      fontSize: 32,
      fontWeight: theme.typography.fontWeightBold,
      color: '#181c32',
    },
    rub: {
      color: '#7c97c0',
      fontSize: 17,
      marginLeft: 8,
      fontWeight: theme.typography.fontWeightRegular,
    },
    containerPrice: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
    },
    btn: {
      height: 48,
      marginLeft: 15,
    },
  };
});
